// @owners { team: patient-health }
import { SPACING } from '@alto/design-library-tokens';
// eslint-disable-next-line @alto/no-pocky-import
import {
  Button,
  DeprecatedModal,
  DeprecatedModalBody,
  DeprecatedModalFooter,
  InputBlockRadio,
  InputBlockText,
  InputRow,
  InputUploadImage,
  RadioOption as PockyRadioOption,
  Tooltip,
} from '@alto/pocky';
import autobind from 'autobind-decorator';
// eslint-disable-next-line @alto/prefer-react-hook-form
import {
  Field,
  type FieldInputProps,
  type FieldMetaProps,
  type FieldProps,
  Form,
  Formik,
  type FormikProps,
} from 'formik';
import React from 'react';
import styled from 'styled-components';
import { type ModalType } from '~shared/actions/modal';
import { GENDERS, PRONOUNS } from '~shared/constants';
import { mapFormikFieldToInputProps } from '~shared/helpers/mapFieldToInputProps';
import { type User } from '~shared/types';

const FlexInputBlockRadio = styled(InputBlockRadio)`
  > :not(:first-child) {
    display: flex;
  }
`;

const RadioOption = styled(PockyRadioOption)`
  margin-right: ${SPACING.STATIC.LG.px};
  margin-bottom: 0;
  display: flex;
`;

type Values = {
  date_of_birth: string;
  first_name: string;
  gender: string;
  last_name: string;
  photo_id_url: string;
  preferred_first_name: string;
  preferred_pronoun: string;
};

export type Props = {
  readonly onSubmit: () => void;
  readonly onClose: () => void;
  readonly user: User | null | undefined;
  readonly modalType: ModalType | null;
  readonly show: boolean;
};

const getRadioOptions = (collection: typeof GENDERS | typeof PRONOUNS) => {
  return Object.entries(collection).map(([key, value]) => {
    return (
      <RadioOption
        name={value}
        value={key}
        key={key}
      >
        {value}
      </RadioOption>
    );
  });
};

class PersonalInfoModal extends React.Component<Props> {
  static defaultProps = {
    modalType: null,
    show: false,
  };

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  onChangePhotoInput(field: FieldInputProps<any>, form: FormikProps<any>) {
    return (files: File[] | null | undefined) => {
      form.setFieldValue(field.name, files);
    };
  }

  render() {
    const { onClose, onSubmit, modalType, show, user } = this.props;
    const shown = show && modalType === 'PERSONAL_INFO_MODAL';

    const nameContent = 'To ensure your privacy, please contact us if you wish to change your name or date of birth';

    const preferredNameContent =
      'If you go by a name other than your legal name, please share it with us so we can address you correctly';
    return (
      shown && (
        <DeprecatedModal
          showing={shown}
          onClose={onClose}
          title="Edit Personal Info"
          scrollable
        >
          <Formik
            initialValues={{
              date_of_birth: user ? user.date_of_birth : '',
              first_name: user ? user.first_name : '',
              gender: user ? user.gender : '',
              last_name: user ? user.last_name : '',
              photo_id_url: user ? user.photo_id_url : '',
              preferred_first_name: user ? user.preferred_first_name : '',
              preferred_pronoun: user ? user.preferred_pronoun : '',
            }}
            onSubmit={onSubmit}
          >
            <Form>
              <DeprecatedModalBody>
                <Tooltip
                  position="top"
                  content={nameContent}
                >
                  <InputRow>
                    <Field name="first_name">
                      {({ field, meta }: FieldProps<string, Values>) => {
                        return (
                          <InputBlockText
                            label="First Name"
                            type="text"
                            placeholder="First"
                            meta={meta}
                            {...field}
                            disabled
                          />
                        );
                      }}
                    </Field>
                  </InputRow>
                </Tooltip>
                <Tooltip
                  position="top"
                  content={nameContent}
                >
                  <InputRow>
                    <Field name="last_name">
                      {({ field, meta }: FieldProps<string, Values>) => {
                        return (
                          <InputBlockText
                            label="Last Name"
                            type="text"
                            placeholder="Last"
                            meta={meta}
                            {...field}
                            disabled
                          />
                        );
                      }}
                    </Field>
                  </InputRow>
                </Tooltip>
                <Tooltip
                  position="top"
                  content={preferredNameContent}
                >
                  <InputRow>
                    <Field name="preferred_first_name">
                      {({ field, meta }: FieldProps<string, Values>) => {
                        return (
                          <InputBlockText
                            label="Preferred name"
                            type="text"
                            placeholder="Preferred name"
                            meta={meta}
                            {...field}
                          />
                        );
                      }}
                    </Field>
                  </InputRow>
                </Tooltip>
                <Tooltip
                  position="top"
                  content={nameContent}
                >
                  <InputRow>
                    <Field name="date_of_birth">
                      {({ field, meta }: FieldProps<string, Values>) => {
                        return (
                          <InputBlockText
                            label="Date of birth"
                            type="text"
                            meta={meta}
                            {...field}
                            disabled
                          />
                        );
                      }}
                    </Field>
                  </InputRow>
                </Tooltip>
                <InputRow>
                  <Field name="gender">
                    {({ field, meta }: FieldProps<string, Values>) => {
                      return (
                        <FlexInputBlockRadio
                          required
                          label="Choose sex"
                          input={field}
                          {...mapFormikFieldToInputProps(field, meta)}
                        >
                          {getRadioOptions(GENDERS)}
                        </FlexInputBlockRadio>
                      );
                    }}
                  </Field>
                </InputRow>
                <InputRow>
                  <Field name="preferred_pronoun">
                    {({ field, meta }: { field: FieldInputProps<Values>; meta: FieldMetaProps<Values> }) => {
                      return (
                        <FlexInputBlockRadio
                          required
                          label="Choose pronoun"
                          // @ts-expect-error:  Type 'Values' is not assignable to type 'string | number'
                          input={field}
                          {...mapFormikFieldToInputProps(field, meta)}
                        >
                          {getRadioOptions(PRONOUNS)}
                        </FlexInputBlockRadio>
                      );
                    }}
                  </Field>
                </InputRow>
                <Field name="photo_id_url">
                  {({ field, meta, form }: FieldProps<Values['photo_id_url']>) => (
                    <InputUploadImage
                      label="Upload photo ID"
                      id="photo-id-upload"
                      uploadText="+ Add photo ID"
                      meta={meta}
                      value={field.value}
                      onChange={this.onChangePhotoInput(field, form)}
                      required
                    />
                  )}
                </Field>
              </DeprecatedModalBody>
              <DeprecatedModalFooter>
                <Button
                  kind="tertiary"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                {/* @ts-expect-error: Property 'onClick' is missing in type */}
                <Button type="submit">Save</Button>
              </DeprecatedModalFooter>
            </Form>
          </Formik>
        </DeprecatedModal>
      )
    );
  }
}

export default PersonalInfoModal;
